import {Component, inject, OnInit} from '@angular/core';
import {Agent, AgentData} from '../service/agent.model';
import {GridEventOutput, GridModel} from 'src/app/shared/model/page-grid';
import {AgentGridService} from '../service/agent-grid.service';
import {AgentService} from '../service/agent.service';
import {MatDialog} from '@angular/material/dialog';
import {AgentInfoModalComponent} from '../info-modal/agent-info-modal.component';
import {SplitRateModalComponent} from '../split-rate-modal/split-rate-modal.component';
import {TramsDataSourceModel} from 'src/app/payments/models/trams-data-source';
import {TramsDataSourceService} from 'src/app/payments/service/trams-data-source.service';
import {map} from 'rxjs/operators';

@Component({
    selector: 'app-agent-list',
    templateUrl: './agent-list.component.html',
    standalone: false
})
export class AgentListComponent implements OnInit {
  private dialog = inject(MatDialog);
  private dataSourceService = inject(TramsDataSourceService);
  private agentService = inject(AgentService);
  private agentGridService = inject(AgentGridService);
  private tramsDataSources: TramsDataSourceModel[] = [];
  ngOnInit(): void {
    this.dataSourceService.getTramsDataSourceList().subscribe({
      next: (sources: TramsDataSourceModel[]) => {
        this.tramsDataSources = sources;
      },
      error: (err) => {
        console.error("Error loading data sources:", err);
      }
    });
  }

  protected readonly agentsGrid: GridModel<AgentData> = {
    gridDataTestId: 'agent-list',
    gridTitle: 'Agents',
    replayButton: true,
    singleColumnFilter: true,
    columns: [
      {
        columnProperty: 'id',
        columnHeader: 'Fms Agent Number',
        sort: true,
        clickable: true,
      },
      {
        columnProperty: 'agentName',
        columnHeader: 'Agent Name',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'interfaceId',
        columnHeader: 'Agent Code',
        sort: true,
      },
      {
        columnProperty: 'email',
        columnHeader: 'Email',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'currentSplitRate',
        columnHeader: 'Split Rate',
        sort: true,
        clickable: true,
        columnPropertyFunction: (agent: Agent) => `${agent.currentSplitRate ? agent.currentSplitRate+'%' : ''}`,
      },
      {
        columnProperty: 'primaryProfile',
        columnHeader: 'Primary Profile',
        sort: true,
        columnPropertyFunction: (agent: Agent) => `${agent.primaryProfile === 1 ? 'Yes' : ''}`,
      },
      {
        columnProperty: 'dataSourceId',
        columnHeader: 'Data Source',
        sort: true,
        columnPropertyFunction: (agent: Agent) => {
          if (agent.tramsDataSource?.sourceName) {
            return agent.tramsDataSource.sourceName;
          }
          const dataSourceId = agent.dataSourceId;
          if (dataSourceId) {
            const dataSource = this.tramsDataSources.find(ds => ds.id === dataSourceId);
            if (dataSource) {
              return dataSource.sourceName;
            }
          }
          return dataSourceId ? `${dataSourceId}` : '';
        },
      },
      {
        columnProperty: 'tramsCentralDocumentId',
        columnHeader: 'Trams Central Document Number',
        sort: true,
      },
      {
        columnProperty: 'branchNo',
        columnHeader: 'Branch',
        sort: true,
        columnPropertyFunction: (agent: Agent) => {
          if (agent.branchNo && agent.branchName) return `${agent.branchNo} - ${agent.branchName}`;
          if (agent.branchNo) return `${agent.branchNo}`;
          return '';
        },
      },
    ],
    filters: [
      {
        filterProperty: 'agentName',
        filterDisplayName: 'Agent Name',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Agent',
          autocompleteColumnName: 'AgentName',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'agentCode',
        filterDisplayName: 'Agent Code',
        filterType: 'AUTOCOMPLETE',
        config: {
          autocompleteTableName: 'Agent',
          autocompleteColumnName: 'InterfaceId',
          autocompleteEmitText: true
        }
      },
      {
        filterProperty: 'dataSourceId',
        filterDisplayName: 'Data Source',
        filterType: 'DROPDOWN',
        config: {
          dropdownOptions: this.dataSourceService.getTramsDataSourceList(),
          dropdownValueProp: 'id',
          dropdownDisplayWith: (item: TramsDataSourceModel) => item.sourceName
        }
      },
    ],
    gridGetService: {
      getItems: (params: any) => {
        return this.agentGridService.getItems(params).pipe(
          map(result => {
            if (result && Array.isArray(result.items)) {
              for (let i = 0; i < result.items.length; i++) {
                const agent = result.items[i] as unknown as Agent;
                const dataSourceId = agent.dataSourceId;
                const dataSource = dataSourceId ? this.tramsDataSources.find(ds => ds.id === dataSourceId) : undefined;
                
                (agent as any).tramsDataSource = dataSource 
                  ? {
                    sourceName: dataSource.sourceName,
                    displayName: dataSource.displayName || '',
                    currencyCode: dataSource.currencyCode || ''
                  }
                  : {
                    sourceName: dataSourceId ? `${dataSourceId}` : '',
                    displayName: '',
                    currencyCode: ''
                  };
              }
            }
            return result;
          })
        );
      }
    }
  };
  protected openDialog(event: GridEventOutput<Agent>): void {
    const item = event.item;
    const column = event.column;
    if(column === 'id') {
      this.openAgentInfoDialog(item, 'Agent Information');
    }
    if(column === 'currentSplitRate') {
      this.openSplitRateDialog(item);
    }
  }
  
  protected openAgentInfoDialog(record: Agent, titleHeader: string): void {
    this.agentService.getAgentById(record.id).subscribe({
      next: (completeAgentData: Agent) => {
        this.dialog.open(AgentInfoModalComponent, {
          width: '55%',
          data: { title: titleHeader, agentData: completeAgentData }
        });
      },
      error: (error) => {
        console.error('Error loading agent details:', error);
        this.dialog.open(AgentInfoModalComponent, {
          width: '55%',
          data: { title: titleHeader, agentData: record }
        });
      }
    });
  }
  
  protected openSplitRateDialog(record: Agent): void {
    this.dialog.open(SplitRateModalComponent, {
      width: '55%',
      data: { agentId: record.id }
    });
  }
}
